import {
  createUsersQueryOptions,
  ListUsersParams,
  useUsersQuery,
} from "@/api/users.api";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { FocusableText } from "@/components/generic/FocusableText";
import { Search } from "@/components/generic/Search";
import {
  SelectFilter,
  useSelectFilter,
} from "@/components/generic/SelectFilter";
import { Separator } from "@/components/generic/Separator";
import { createTypedTable } from "@/components/generic/Table";
import { PageHeader } from "@/components/layout/PageHeader";
import { StickyElement } from "@/components/layout/StickyElement";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { useTrialPeriod } from "@/hooks/use-trial-period";
import {
  getUserRoleLevel,
  hasInsightsAccess,
  isLassoAdmin,
} from "@/services/auth.service";
import { User } from "@/types/auth";
import { UserRole } from "@/types/user";
import {
  getCurrentContract,
  getLicenseData,
} from "@/utils/organization-helpers";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortDescriptor } from "react-aria-components";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";
import { z } from "zod";

const searchParamsSchema = z.object({
  status: z
    .union([z.literal("all"), z.literal("active"), z.literal("inactive")])
    .optional(),
  role: z
    .union([z.literal("all"), z.literal("sales"), z.literal("salesleader")])
    .optional(),
  insightsAccess: z
    .union([z.literal("all"), z.literal("yes"), z.literal("no")])
    .optional(),
});

type SearchParams = z.infer<typeof searchParamsSchema>;

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/users/_users/",
)({
  component: RouteComponent,
  validateSearch: (search: SearchParams) => searchParamsSchema.parse(search),
  beforeLoad: ({ search }) => {
    const status = search.status ?? "all";
    const role = search.role ?? "all";
    const insightsAccess = search.insightsAccess ?? "all";

    const queryParams: ListUsersParams & {
      role?: string;
      insightsAccess?: string;
      type?: string;
    } = {
      status: status,
      role: role,
      insightsAccess: insightsAccess,
      nameFilter: "",
      page: 1,
    };

    return {
      search: {
        ...search,
        status,
        role,
        insightsAccess,
      },
      queryParams,
      usersQueryOptions: createUsersQueryOptions(queryParams),
    };
  },
  loader: async ({ context }) => {
    context.queryClient.prefetchInfiniteQuery(context.usersQueryOptions);
  },
});

const typedTable = createTypedTable<User>();

type LicenseModalType =
  | "pending-contract"
  | "no-licenses-no-price"
  | "buy-more-licenses"
  | null;

function RouteComponent() {
  const context = Route.useRouteContext();
  const search = context.search;
  const navigate = Route.useNavigate();

  const [searchTerm, setSearchTerm] = useState<string>("");

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "name",
    direction: "ascending",
  });

  const [hoveredId, setHoveredId] = useState<string | null | undefined>(null);

  const [licenseModalType, setLicenseModalType] =
    useState<LicenseModalType>(null);

  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  const isInitialMount = useRef(true);

  const userStateFilter = useSelectFilter({
    items: [
      { key: "all", label: "Alle", value: "all" as const },
      { key: "active", label: "Aktive brugere", value: "active" as const },
      {
        key: "inactive",
        label: "Deaktiverede brugere",
        value: "inactive" as const,
      },
    ],
    defaultSelectedKeys: new Set([search.status]),
  });

  const roleStateFilter = useSelectFilter({
    items: [
      { key: "all", label: "Alle roller", value: "all" as const },
      { key: "sales", label: "Sælger", value: "sales" as const },
      {
        key: "salesleader",
        label: "Salgsleder",
        value: "salesleader" as const,
      },
    ],
    defaultSelectedKeys: new Set([search.role || "all"]),
  });

  const insightsAccessFilter = useSelectFilter({
    items: [
      { key: "all", label: "Alle", value: "all" as const },
      { key: "yes", label: "Ja", value: "yes" as const },
      { key: "no", label: "Nej", value: "no" as const },
    ],
    defaultSelectedKeys: new Set([search.insightsAccess || "all"]),
  });

  const userState = useMemo(
    () => userStateFilter.selectedValues[0],
    [userStateFilter.selectedValues],
  );

  const roleState = useMemo(
    () => roleStateFilter.selectedValues[0],
    [roleStateFilter.selectedValues],
  );

  const insightsAccessState = useMemo(
    () => insightsAccessFilter.selectedValues[0],
    [insightsAccessFilter.selectedValues],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      userState !== search.status ||
      roleState !== search.role ||
      insightsAccessState !== search.insightsAccess
    ) {
      navigate({
        search: {
          status: userState,
          role: roleState,
          insightsAccess: insightsAccessState,
        },
      });
    }
  }, [userState, roleState, insightsAccessState, search]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { ref, isIntersecting } = useIntersectionObserver();

  const completeQueryOptions = useMemo(() => {
    return createUsersQueryOptions({
      ...context.queryParams,
      nameFilter: debouncedSearchTerm,
      status: userState,
      role: roleState,
      insightsAccess: insightsAccessState,
    });
  }, [
    context.queryParams,
    debouncedSearchTerm,
    userState,
    roleState,
    insightsAccessState,
  ]);

  const { data, isFetching, fetchNextPage } = useUsersQuery({
    nameFilter: debouncedSearchTerm,
    status: userState,
    role: roleState,
    insightsAccess: insightsAccessState,
  });

  const allUsers = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data],
  );

  const { TableHeader, TableBody } = typedTable;

  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(bodyScrollRef, headerScrollRef);

  const userRole = (roles: Set<string>) => {
    if (roles.has(UserRole.LassoAdmin)) {
      return "Lasso Admin";
    }
    if (roles.has(UserRole.Admin)) {
      return "Administrator";
    }
    if (roles.has(UserRole.Salesleader)) {
      return "Salgsleder";
    }
    if (roles.has(UserRole.Sales)) {
      let role = "Sælger";
      if (roles.has(UserRole.SalesAllCampaignAccess)) {
        role += " med adgang til alle kampagner";
      }
      return role;
    }
    return "-";
  };

  const columns = typedTable.createColumns([
    {
      id: "name",
      name: "Navn",
      width: 130,
      renderCell: (user) => {
        return (
          <div className="flex items-center gap-3">
            <span
              className={twMerge(
                `w-[135px]`,
                (!user.emailConfirmed || !user.active) && "truncate",
              )}
            >
              {user.name}
            </span>
            {!user.emailConfirmed && (
              <span className="text-green-600 dark:text-green-400">
                Inviteret
              </span>
            )}
            {!user.active && (
              <span className="text-theme-on-glass-variant">Deaktiveret</span>
            )}
          </div>
        );
      },
    },
    {
      id: "email",
      name: "Email",
      width: 100,
    },
    {
      id: "roles",
      name: "Rolle",
      width: 100,
      renderCell: (user) => {
        return <FocusableText text={userRole(new Set(user.roles))} />;
      },
    },

    {
      id: "accessToInsights",
      name: "Adgang til Insights",
      width: 80,
      renderCell: (user) => {
        return hasInsightsAccess(user) ? (
          <span>Ja</span>
        ) : (
          <span className="text-theme-on-background-variant">Nej</span>
        );
      },
    },
  ]);

  const filteredUsers = allUsers;

  const { currentLicenses, licensePrice, remainingLicenses } = getLicenseData(
    context.user!.organization,
  );

  const currentContract = getCurrentContract(context.user!.organization);
  const hasPendingLicenseAddition =
    currentContract && currentContract.pendingLicenseAddition;

  const trialPeriod = useTrialPeriod();

  useEffect(() => {
    if (isIntersecting) fetchNextPage();
  }, [fetchNextPage, isIntersecting]);

  const handleAddUserClick = useCallback(() => {
    if (trialPeriod?.isExpired) {
      setShowTrialExpiredModal(true);
      return;
    }

    if (trialPeriod) {
      navigate({
        to: "/settings/users/invite-new",
      });
      return;
    }

    if (hasPendingLicenseAddition) {
      setLicenseModalType("pending-contract");
      return;
    }

    if (remainingLicenses <= 0 && licensePrice === null) {
      setLicenseModalType("no-licenses-no-price");
      return;
    }

    if (remainingLicenses <= 0 && licensePrice !== null) {
      setLicenseModalType("buy-more-licenses");
      return;
    }

    navigate({
      to: "/settings/users/invite-new",
    });
  }, [
    trialPeriod,
    hasPendingLicenseAddition,
    remainingLicenses,
    licensePrice,
    navigate,
  ]);

  const handleBuyMoreLicenses = useCallback(() => {
    setLicenseModalType(null);

    navigate({
      to: "/settings/users/add-users",
    });
  }, [navigate]);

  const onClickItem = useCallback(
    (item: User) => {
      if (item.id === context.user.id) {
        navigate({
          to: "/settings/profile",
        });
        return;
      }

      const currentUserRoleLevel = getUserRoleLevel(context.user);
      const targetUserRoleLevel = getUserRoleLevel(item);

      if (targetUserRoleLevel > currentUserRoleLevel || isLassoAdmin(item)) {
        toast("Du har ikke adgang til at redigere denne bruger");
        return;
      }

      navigate({
        to: "/settings/users/$id",
        params: { id: item.id },
      });
    },
    [context.user, navigate],
  );

  return (
    <div className="flex h-full flex-col pt-0">
      <PageHeader
        title="Brugere"
        actions={
          <Button
            onClick={() => {
              handleAddUserClick();
            }}
            animate
            className="px-8"
          >
            Inviter ny bruger
          </Button>
        }
      />
      <div className="relative mb-5 grid *:col-start-1 *:row-start-1">
        <typedTable.Table
          columns={columns}
          items={filteredUsers}
          sortDescriptor={sortDescriptor}
          setSortDescriptor={setSortDescriptor}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          aria-label="Brugertabel"
          onClickItem={onClickItem}
        >
          <StickyElement
            className="px-8 xl:px-14"
            stickyProps={{ className: "bg-theme-background" }}
          >
            <div className="my-4 flex w-full items-center gap-x-4">
              <Search
                className="-ml-2 grow-1"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
              />

              <SelectFilter
                aria-label="Vælg rolle"
                selectionMode="single"
                buttonLabel="Role"
                disallowEmptySelection={true}
                items={roleStateFilter.items}
                selectedKeys={roleStateFilter.selectedKeys}
                onSelectionChange={roleStateFilter.setSelectedKeys}
              />

              <SelectFilter
                aria-label="Vælg brugerstatus"
                selectionMode="single"
                buttonLabel="Status"
                disallowEmptySelection={true}
                items={userStateFilter.items}
                selectedKeys={userStateFilter.selectedKeys}
                onSelectionChange={userStateFilter.setSelectedKeys}
              />
            </div>
            <Separator />
          </StickyElement>
          <TableHeader
            id="scroll-headers"
            className="scrollbar-none mt-3 min-w-0 overflow-x-scroll xl:mt-4"
            ref={headerScrollRef}
          />
          <TableBody
            id="scroll-headers"
            ref={bodyScrollRef}
            className={twMerge(
              isFetching && "opacity-50",
              "min-w-0 cursor-pointer overflow-x-scroll transition-opacity duration-200",
            )}
          />
        </typedTable.Table>
      </div>

      <AlertModal
        isOpen={licenseModalType === "pending-contract"}
        onClose={() => setLicenseModalType(null)}
        size="md"
        iconDisable={true}
        buttonChildren={
          <Button
            size="lg"
            animate
            onClick={() => {
              setLicenseModalType(null);
            }}
          >
            Luk
          </Button>
        }
        title="Afventer kontraktgodkendelse"
        description="Du har afventende tillægskontrakt, som skal godkendes før du kan tilkøbe nye brugere."
      />

      <AlertModal
        isOpen={licenseModalType === "no-licenses-no-price"}
        onClose={() => {
          setLicenseModalType(null);
        }}
        iconDisable={true}
        buttonChildren={
          <Button
            size="lg"
            animate
            onClick={() => {
              setLicenseModalType(null);
            }}
          >
            Luk
          </Button>
        }
        size="md"
        title="Ikke flere ledige pladser"
        description="Du har ikke flere ledige pladser. Kontakt os på tlf. 71747812 eller contact@lassox.com, for at få flere brugere."
      />

      <AlertModal
        isOpen={licenseModalType === "buy-more-licenses"}
        title="Tilkøb flere brugere"
        description={`Du har brugt alle ledige pladser. Tilkøb brugere, for at kunne invitere flere. Prisen er ${licensePrice} kr. pr. ekstra bruger.`}
        onConfirm={handleBuyMoreLicenses}
        onClose={() => setLicenseModalType(null)}
        size="md"
        buttonChildren={
          <div className="flex gap-6">
            <Button
              size="lg"
              intent="secondary"
              onClick={() => setLicenseModalType(null)}
              animate
            >
              Fortryd
            </Button>
            <Button size="lg" animate onClick={handleBuyMoreLicenses}>
              Køb brugere
            </Button>
          </div>
        }
        iconDisable={true}
      />

      <TrialCTAModal
        isOpen={showTrialExpiredModal}
        onOpenChange={setShowTrialExpiredModal}
        title="Din prøveperiode er udløbet. Kontakt os for at få adgang til Lasso Sales uden begrænsninger."
      />
    </div>
  );
}
